// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  ProviderSubmissionDTO,
  ProviderAccessTokenDTO,
  SubmissionReviewRequest,
  SubmissionReviewResult,
  TokenRevocationResult,
} from '../types/providerAccess';

/**
 * Authenticated API client for advisor review of provider submissions and access tokens.
 * Uses the shared advisorApi instance (JWT + X-Brokerage-Id).
 */
export const providerSubmissionApi = {
  /** List all provider submissions for a policy */
  listSubmissions: async (policyId: string): Promise<ProviderSubmissionDTO[]> => {
    const response = await api.get<ProviderSubmissionDTO[]>(
      `/policies/${policyId}/provider-submissions`,
    );
    return response.data;
  },

  /** Download a submitted document as a blob */
  downloadDocument: async (policyId: string, submissionId: string): Promise<Blob> => {
    const response = await api.get(`/policies/${policyId}/provider-submissions/${submissionId}/download`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  /** Mark a submission as reviewed */
  reviewSubmission: async (
    policyId: string,
    submissionId: string,
    request?: SubmissionReviewRequest,
  ): Promise<SubmissionReviewResult> => {
    const response = await api.post<SubmissionReviewResult>(
      `/policies/${policyId}/provider-submissions/${submissionId}/review`,
      request || {},
    );
    return response.data;
  },

  /** Get the latest FORM-type submission for metadata merge */
  getLatestFormSubmission: async (policyId: string): Promise<ProviderSubmissionDTO | null> => {
    try {
      const response = await api.get<ProviderSubmissionDTO>(
        `/policies/${policyId}/provider-submissions/latest-form`,
      );
      return response.data;
    } catch (err: unknown) {
      const error = err as { response?: { status?: number } };
      if (error.response?.status === 204) return null;
      throw err;
    }
  },

  /** List all access tokens for a policy */
  listAccessTokens: async (policyId: string): Promise<ProviderAccessTokenDTO[]> => {
    const response = await api.get<ProviderAccessTokenDTO[]>(
      `/policies/${policyId}/access-tokens`,
    );
    return response.data;
  },

  /** Revoke an active access token */
  revokeToken: async (policyId: string, tokenId: string): Promise<TokenRevocationResult> => {
    const response = await api.post<TokenRevocationResult>(
      `/policies/${policyId}/access-tokens/${tokenId}/revoke`,
    );
    return response.data;
  },
};
